// ============================================
// PAGE - Caja 360º Home
// ============================================

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MdPointOfSale, MdManageAccounts } from 'react-icons/md';
import { useAuthStore, useMisPermisos } from '@almacenes/core';
import { useShepherdTour, TourFab, type TourStepConfig } from '@almacenes/ui';
import { useTourPersistido } from '@/hooks/useTourPersistido';
import logo from '@/assets/logos/nuevo.svg';

const TOUR_ID = 'caja-360-home';
const TOUR_VERSION = 'v1';

interface ModuloCaja {
  name: string;
  description: string;
  icon: typeof MdPointOfSale;
  href: string;
  bgColor: string;
  iconColor: string;
  soloPropietario?: boolean;
}

const modules: ModuloCaja[] = [
  {
    name: 'Mi caja',
    description: 'Abrir, ver en vivo y cerrar mi turno',
    icon: MdPointOfSale,
    href: '/caja/mi-caja',
    bgColor: '#FEF3C7',
    iconColor: '#D97706',
  },
  {
    name: 'Administrar cajas',
    description: 'Turnos abiertos, historial, aprobaciones y configuración',
    icon: MdManageAccounts,
    href: '/caja/administracion',
    bgColor: '#E8F4FD',
    iconColor: '#43BFFF',
    soloPropietario: true,
  },
];

const buildSteps = (mostrarStepAdministrar: boolean): TourStepConfig[] => {
  const steps: TourStepConfig[] = [
    {
      id: 'intro',
      title: 'Bienvenido a Caja 360º',
      text: 'Rediseñamos por completo el módulo de caja. Te mostramos rápido dónde quedó cada cosa.',
      attachTo: { element: '[data-tour="caja-header"]', on: 'bottom' },
    },
    {
      id: 'mi-caja',
      title: 'Mi caja',
      text: 'Acá abres, ves en vivo y cierras tu propio turno — reemplaza a la apertura/cierre de caja de antes.',
      attachTo: { element: '[data-tour="caja-mi-caja"]', on: 'bottom' },
    },
  ];

  if (mostrarStepAdministrar) {
    steps.push({
      id: 'administrar',
      title: 'Administrar cajas',
      text: 'Turnos abiertos del almacén, historial, aprobaciones de movimientos y configuración — según los permisos que tengas.',
      attachTo: { element: '[data-tour="caja-administrar"]', on: 'bottom' },
    });
  }

  return steps;
};

export const CajaHomePage = () => {
  const session = useAuthStore((state) => state.session);
  const esPropietario = session?.user?.esPropietario ?? false;
  // Un vendedor con algún permiso de caja otorgado también entra acá, no
  // solo el propietario — useMisPermisos() ya devuelve el catálogo completo
  // cuando esPropietario es true, así que alcanza con mirar la lista.
  const { data: misPermisos } = useMisPermisos();
  const puedeAdministrarCaja = esPropietario || (misPermisos?.length ?? 0) > 0;

  const modulosVisibles = modules.filter((m) => !m.soloPropietario || puedeAdministrarCaja);

  const steps = useMemo(() => buildSteps(puedeAdministrarCaja), [puedeAdministrarCaja]);
  const { start } = useShepherdTour(steps, { logoUrl: logo, name: 'Almacenes Digitales' });
  const { seen, markAsSeen } = useTourPersistido(TOUR_ID, TOUR_VERSION);

  useEffect(() => {
    if (!seen) {
      start();
      markAsSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen]);

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      <TourFab onClick={start} />

      {/* Header */}
      <div className="mb-6" data-tour="caja-header">
        <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>
          Caja 360º
        </h1>
        <p className="text-gray-600 mt-2">
          Gestiona las operaciones de caja de tu almacén
        </p>
      </div>

      {/* Módulos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {modulosVisibles.map((module) => {
          const Icon = module.icon;
          const dataTour = module.href === '/caja/mi-caja' ? 'caja-mi-caja' : 'caja-administrar';

          return (
            <Link
              key={module.name}
              to={module.href}
              className="group"
              data-tour={dataTour}
            >
              <div
                className="bg-white rounded-xl p-6 border border-gray-200 h-full transition-all duration-300 hover:shadow-lg hover:scale-105"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  {/* Icono */}
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: module.bgColor }}
                  >
                    <Icon
                      className="text-5xl"
                      style={{ color: module.iconColor }}
                    />
                  </div>

                  {/* Título */}
                  <h3
                    className="font-bold text-2xl"
                    style={{ color: '#11194F' }}
                  >
                    {module.name}
                  </h3>

                  {/* Descripción */}
                  <p className="text-gray-600">
                    {module.description}
                  </p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};
